// src/pages/SalaryManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Refresh as RefreshIcon, NotificationImportant as NotificationImportantIcon } from '@mui/icons-material';

const API_URL = "https://htm.bayex.lk/api/salary_management.php";

export default function SalaryManagement() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const today     = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [salarySlips, setSalarySlips] = useState([]);

  // Detail dialog state
  const [detailSlip, setDetailSlip]       = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Fetch data
  const fetchSalaryData = () => {
    fetch(`${API_URL}?year=${year}&month=${month}`)
      .then(r => r.json())
      .then(setSalarySlips)
      .catch(console.error);
  };
  useEffect(fetchSalaryData, [year, month]);

  // Open/close detail
  const openDetail = slip => {
    setDetailSlip(slip);
    setDetailModalOpen(true);
  };
  const closeDetail = () => {
    setDetailModalOpen(false);
    setDetailSlip(null);
  };

  // CSV download
  const downloadCSV = () => {
    const headers = [
      "Basic Salary/Day",
      "Staff Name",
      "Days Present",
      "Basic Earning",
      "Tips+Svc Earning",
      "Loan Deduction",
      "Total Salary"
    ];
    const rows = salarySlips.map(slip => [
      slip.basic_salary_per_day.toFixed(2),
      slip.staff_name,
      slip.days_present,
      slip.basic_earning.toFixed(2),
      slip.extra_earning.toFixed(2),
      (slip.loan_deduction||0).toFixed(2),
      slip.total_salary.toFixed(2),
    ]);
    let csv = headers.join(",") + "\n";
    rows.forEach(r => { csv += r.join(",") + "\n"; });
    const blob = new Blob([csv], { type:"text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href       = url;
    a.download   = `paysheet_${year}_${month}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Salary Management
      </Typography>

      {/* Filters and actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Year"
          type="number"
          size="small"
          value={year}
          onChange={e => setYear(+e.target.value)}
        />
        <TextField
          label="Month"
          type="number"
          size="small"
          inputProps={{ min:1, max:12 }}
          value={month}
          onChange={e => setMonth(+e.target.value)}
        />
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchSalaryData}>
          Refresh
        </Button>
        {salarySlips.length > 0 && (
          <Button variant="outlined" onClick={downloadCSV}>
            Download Paysheet
          </Button>
        )}
      </Box>

      {salarySlips.length > 0 ? (
        isMobile ? (
          /* Mobile cards */
          <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
            {salarySlips.map(slip => (
              <Card
                key={slip.staff_id}
                variant="outlined"
                sx={{ backgroundColor: '#fafafa', cursor: 'pointer' }}
                onClick={() => openDetail(slip)}
              >
                <CardContent>
                  <Typography variant="h6">{slip.staff_name}</Typography>
                  <Typography><strong>Basic/Day:</strong> {slip.basic_salary_per_day.toFixed(2)}</Typography>
                  <Typography><strong>Days Present:</strong> {slip.days_present}</Typography>
                  <Typography><strong>Basic:</strong> {slip.basic_earning.toFixed(2)}</Typography>
                  <Typography><strong>Tips+Svc:</strong> {slip.extra_earning.toFixed(2)}</Typography>
                  <Typography><strong>Loan Deduct:</strong> {(slip.loan_deduction||0).toFixed(2)}</Typography>
                  <Typography><strong>Total:</strong> {slip.total_salary.toFixed(2)}</Typography>
                </CardContent>
                <CardActions>
                  <IconButton onClick={() => openDetail(slip)}>
                    <NotificationImportantIcon />
                  </IconButton>
                </CardActions>
              </Card>
            ))}
          </Box>
        ) : (
          /* Desktop table with Actions column */
          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Basic/Day</TableCell>
                  <TableCell>Staff</TableCell>
                  <TableCell align="right">Days Present</TableCell>
                  <TableCell align="right">Basic Earning</TableCell>
                  <TableCell align="right">Tips + Svc Earning</TableCell>
                  <TableCell align="right">Loan Deduction</TableCell>
                  <TableCell align="right">Total Salary</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {salarySlips.map(slip => (
                  <TableRow key={slip.staff_id}>
                    <TableCell>{slip.basic_salary_per_day.toFixed(2)}</TableCell>
                    <TableCell>{slip.staff_name}</TableCell>
                    <TableCell align="right">{slip.days_present}</TableCell>
                    <TableCell align="right">{slip.basic_earning.toFixed(2)}</TableCell>
                    <TableCell align="right">{slip.extra_earning.toFixed(2)}</TableCell>
                    <TableCell align="right">{(slip.loan_deduction||0).toFixed(2)}</TableCell>
                    <TableCell align="right">{slip.total_salary.toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => openDetail(slip)}>
                        <NotificationImportantIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )
      ) : (
        <Typography>No salary data found for the selected period.</Typography>
      )}

      {/* Detail Dialog */}
      {detailSlip && (
        <Dialog open={detailModalOpen} onClose={closeDetail} fullWidth>
          <DialogTitle>Salary Slip Detail</DialogTitle>
          <DialogContent dividers>
            <Typography variant="h6">{detailSlip.staff_name}</Typography>
            <Typography><strong>Basic/Day:</strong> {detailSlip.basic_salary_per_day.toFixed(2)}</Typography>
            <Typography><strong>Days Present:</strong> {detailSlip.days_present}</Typography>
            <Typography><strong>Basic Earning:</strong> {detailSlip.basic_earning.toFixed(2)}</Typography>
            <Typography><strong>Tips + Svc Earning:</strong> {detailSlip.extra_earning.toFixed(2)}</Typography>
            <Typography><strong>Loan Deduction:</strong> {(detailSlip.loan_deduction||0).toFixed(2)}</Typography>
            <Typography><strong>Total Salary:</strong> {detailSlip.total_salary.toFixed(2)}</Typography>
            <Box sx={{ mt:2 }}>
              <Typography variant="subtitle1">Daily Breakdown</Typography>
              {detailSlip.daily_breakdown.map((d, i) => (
                <Box key={i} sx={{ mb:1 }}>
                  <Typography>
                    <strong>{d.date}</strong> — Basic: {d.daily_basic.toFixed(2)}, Extra: {d.daily_extra.toFixed(2)}, Total: {d.daily_total.toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDetail}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
